import React, { Dispatch, FC, SetStateAction, useEffect, useState } from "react";

import { useLayout } from "../../hooks/use-layout";
import { Application } from "../../models/application";
import {
  ActionModalConfirmation,
  ActionModalInfo,
  ApplicationWithCategoryType,
  CategoryType
} from "../../pages/applications/applications";

import { Labels } from "../../models/labels";

import { FaIcon, FaIcons, Link, Table, Tag, useMediaQuery } from "@in/component-library";
import { KeyedMutator } from "swr";
import ApplicationActions from "../application-actions/application-actions";

enum TableHeader {
  Title = "title",
  Type = "type",
  Date = "date",
  Status = "status",
  Options = "options",
  ScrollCue = "scroll-cue"
}

interface ApplicationTableProps {
  applications: ApplicationWithCategoryType[];
  caption: string;
  id: string;
  setActionModalInfo: Dispatch<SetStateAction<ActionModalInfo>>;
  setActionModalConfirmation: Dispatch<SetStateAction<ActionModalConfirmation>>;
  refetchApplications: KeyedMutator<Application[]>;
}

const ApplicationTable: FC<ApplicationTableProps> = ({
  applications,
  caption,
  id,
  setActionModalInfo,
  setActionModalConfirmation,
  refetchApplications
}) => {
  const { translations } = useLayout();
  const [isMobile, setIsMobile] = useState<boolean>();
  const mediaQueryMobile = useMediaQuery("(max-width: 48em)");
  const mobileMatch = mediaQueryMobile ? mediaQueryMobile.matches : undefined;

  // Manage mobile view
  useEffect(() => {
    if (mobileMatch !== undefined && mobileMatch !== isMobile) {
      setIsMobile(mobileMatch);
    }
  }, [mobileMatch, isMobile]);

  const getTableCoulmnHeaders = (headers: TableHeader[]) => {
    const getTranslation = (translation: TableHeader) => {
      switch (translation) {
        case TableHeader.Title:
          return translations.applicationsTableHeaderTitle;
        case TableHeader.Type:
          return translations.applicationsTableHeaderType;
        case TableHeader.Date:
          return translations.applicationsTableHeaderDate;
        case TableHeader.Status:
          return translations.applicationsTableHeaderStatus;
        case TableHeader.Options:
          return translations.applicationsTableHeaderOptions;
        case TableHeader.ScrollCue:
          return { content: " ", "aria-hidden": true };
      }
    };
    return headers.map((header) => getTranslation(header));
  };

  const getTableRows = (headers: TableHeader[]) => {
    return applications.map((application) => {
      const { title, categoryType, lastChange, statusType } = application;

      // Title cell
      const titleCell = (
        <div className="application-table__title-cell-wrapper">
          <Link
            href={
              application.categoryType === CategoryType.Application ? application.projectUrl : application.statusUrl
            }
          >
            {title}
          </Link>
        </div>
      );

      // Type cell
      const typeCell = translations[`applicationsCategoryType${categoryType}` as keyof Labels];

      // Date cell
      const dateCell = lastChange;

      // Status cell
      const getApplicationTagType = (statusType: string): string => {
        switch (statusType) {
          case "Rejected":
          case "Returned":
          case "ReturnedByMunicipality":
          case "Annulled":
          case "OfferExpired":
          case "Withdrawn":
            return "error";
          case "Approved":
            return "success";
          case "UnderConsideration":
          case "UnderConsiderationByMunicipality":
            return "none";
          case "Submitted":
          case "Sparring":
            return "info";
          default:
            return "generic";
        }
      };

      const statusCell = (
        <div className="application-table__status-cell-wrapper">
          <Tag type={getApplicationTagType(statusType)}>{translations[`applicationsStatus${statusType}`]}</Tag>
        </div>
      );

      // Actions cell
      const actionsCell = (
        <ApplicationActions
          application={application}
          setActionModalInfo={setActionModalInfo}
          setActionModalConfirmation={setActionModalConfirmation}
          refetchApplications={refetchApplications}
          isMobile={isMobile}
        />
      );

      // Scroll cue cell
      const scrollCueCell = <div className="application-table__scroll-cue-cell" aria-hidden="true" />;

      const getCell = (translation: TableHeader) => {
        switch (translation) {
          case TableHeader.Title:
            return titleCell;
          case TableHeader.Type:
            return typeCell;
          case TableHeader.Date:
            return dateCell;
          case TableHeader.Status:
            return statusCell;
          case TableHeader.Options:
            return actionsCell;
          case TableHeader.ScrollCue:
            return scrollCueCell;
        }
      };

      return {
        cells: headers.map((header) => getCell(header)),
        id: application.id
      };
    });
  };

  const headersDesktop = [
    TableHeader.Options,
    TableHeader.Title,
    TableHeader.Type,
    TableHeader.Date,
    TableHeader.Status
  ];
  const headersMobile = [
    TableHeader.Options,
    TableHeader.Title,
    TableHeader.Type,
    TableHeader.Date,
    TableHeader.Status,
    TableHeader.ScrollCue
  ];

  const desktopTable = (
    <Table
      id={id}
      hideCaption
      thead={getTableCoulmnHeaders(headersDesktop)}
      caption={caption}
      className="application-table__table--desktop"
      rows={getTableRows(headersDesktop)}
    />
  );

  const mobileTable = (
    <div className="application-table__table-wrapper--mobile">
      <Table
        id={id}
        hideCaption
        thead={getTableCoulmnHeaders(headersMobile)}
        caption={caption}
        className="application-table__table--mobile"
        rows={getTableRows(headersMobile)}
      />
    </div>
  );
  return (
    <>
      {isMobile !== undefined ? (
        <div className="application-table">
          {applications.length > 0 ? (
            isMobile ? (
              mobileTable
            ) : (
              desktopTable
            )
          ) : (
            <div className="application-table__text-block">
              <FaIcon name={FaIcons.FolderOpenLight} size="2x" />
              <h2 className="application-table__text-block-heading">{translations.applicationsEmptyListTitle}</h2>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
};

export default ApplicationTable;
